/** @jsx jsx */
// @flow

import React from 'react';
import _ from 'lodash';
import bemify from 'bemify-js';

import { StaticImage } from '@zapier/common-components';
import { jsx } from '@emotion/core';
import { Typography, Colors } from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/common-utils';

import FeatureItems from 'app/login/components/FeatureItems';
import ZapStepIcons from 'app/common/components/ZapStepIcons';
import matchesMediaQuery from 'app/common/components/matchesMediaQuery';
import 'app/login/components/SignUpPromoTest.scss';

const bem = bemify('sign-up-promo-test');

type ServiceType = {
  id: string,
  name: string,
};

export type SignUpPromoProps = {
  isZt: boolean,
  isOneService: boolean,
  isTwoService: boolean,
  isInvitation: boolean,
  matchesMediaQuery: Object,
  data: Object,
  query: {
    reauth?: boolean,
    /**
     * Query param associated with homepage CTA links
     */
    use_case?: string,
  },
};

const Styles = labelStyles(__filename, {
  title: {
    ...Typography.pageHeader5,
    color: Colors.gray,
    display: 'block',
    marginBottom: 30,
    textAlign: 'center',
    '@media screen and (min-width: 750px)': {
      textAlign: 'left',
    },
  },
  subTitle: ({ isZt, isOneService, isTwoService, isInvitation }) => {
    const isPlainSignup =
      !isZt && !isOneService && !isTwoService && !isInvitation;
    return [
      {
        color: Colors.gray80,
        textAlign: 'center',
        fontSize: '1.6rem',
        fontWeight: '600',
        lineHeight: '2.4rem',
        margin: '0 0 30px',
      },
      !isPlainSignup &&
        withModifier('hidden-signup', {
          '@media screen and (max-width: 749px)': {
            display: 'none',
          },
        }),
    ];
  },
  featureItems: {
    '@media screen and (max-width: 749px)': {
      display: 'none',
    },
  },
});

/**
 * Dynamic headings per homepage CTA
 * These are duplicated from the homepage
 * @see root_views.py context['river']
 */
const useCaseTitles = {
  'send-new-leads-a-personal-message': 'Engage your leads instantly',
  'sync-new-leads-to-a-crm-or-spreadsheet':
    'Automatically import new customers',
  'notify-my-team-about-new-leads': 'Notify your team about opportunities',
};

const getSignUpMessagingData = ({
  isZt,
  isOneService,
  isTwoService,
  isInvitation,
  data,
  query: { use_case },
}: SignUpPromoProps) => {
  if (isZt) {
    return {
      subTitle: _.get(data, ['zapTemplateById', 'title']),
      headService: null,
      tailService: null,
    };
  }

  // If user came through valid homepage CTA, display dynamic title
  if (use_case && useCaseTitles[use_case]) {
    return {
      subTitle: <span css={Styles.title}>{useCaseTitles[use_case]}</span>,
      headService: null,
      tailService: null,
    };
  }

  const headService: ServiceType = _.get(data, ['headService']);
  const tailService: ServiceType = _.get(data, ['tailService']);

  const headServiceName: string = _.get(headService, ['name']);
  const tailServiceName: string = _.get(tailService, ['name']);

  if (isOneService) {
    return {
      subTitle: `Connect ${headServiceName} with the apps you use every day to find your productivity superpowers`,
      headService,
      tailService: null,
    };
  }

  if (isTwoService) {
    return {
      subTitle: `Find your productivity superpowers—connect ${headServiceName} and ${tailServiceName} to automate your work`,
      headService,
      tailService,
    };
  }

  if (isInvitation) {
    return {
      subTitle:
        'Find your productivity superpowers—connect the apps you use everyday to automate your work',
      headService: null,
      tailService: null,
    };
  }

  return {
    subTitle: (
      <span css={Styles.title}>
        Join millions worldwide who automate their work using Zapier.
      </span>
    ),
    headService: null,
    tailService: null,
  };
};

const AppsIllustration = ({ steps, max, isMobile, connectIconType }) => {
  return (
    <React.Fragment>
      <StaticImage
        alt="A little Zapier character, waving"
        className={bem('__zappling')}
        src="/images/applings/zapier-wave-right.svg"
      />
      <div className={bem('__app-container')}>
        <ZapStepIcons
          connectIconType={connectIconType}
          kind={{
            borderless: true,
            large70: steps.length < 3 && !isMobile,
            large50: steps.length < 3 && isMobile,
          }}
          max={max}
          steps={steps}
        />
      </div>
    </React.Fragment>
  );
};

const getApplicableIllustrationNode = (props: SignUpPromoProps) => {
  const { isZt, data } = props;
  const { isMobile } = props.matchesMediaQuery;
  const result = getSignUpMessagingData(props);

  const headService = _.get(result, ['headService']);
  const tailService = _.get(result, ['tailService']);

  // Renders zap template illustration
  if (isZt) {
    const ztSteps = _.get(data, ['zapTemplateById', 'steps'], []);
    const transformedSteps = ztSteps
      .filter(step => step.type === 'node')
      .map(step => ({
        ...step,
        key: step.selected_api || 'unknown',
      }));

    return (
      <AppsIllustration
        connectIconType="zap-arrow"
        isMobile={isMobile}
        max={3}
        steps={transformedSteps}
      />
    );
  }

  // Renders two service illustration
  if (tailService && headService) {
    const steps = [{ key: headService.id }, { key: tailService.id }];

    return (
      <AppsIllustration
        connectIconType="plus"
        isMobile={isMobile}
        max={2}
        steps={steps}
      />
    );
  }

  // Renders single service illustration
  if (headService) {
    const steps = [
      {
        key: headService.id,
      },
    ];

    return (
      <AppsIllustration
        connectIconType="plus"
        isMobile={isMobile}
        max={1}
        steps={steps}
      />
    );
  }

  return null;
};

const SignUpPromo = (props: SignUpPromoProps) => {
  const result = getSignUpMessagingData(props);
  const { subTitle } = result;

  const illustrationNode = getApplicableIllustrationNode(props);

  return (
    <div className={bem()}>
      {illustrationNode && (
        <div className={bem('__illustration-container')}>
          {illustrationNode}
        </div>
      )}
      <p css={Styles.subTitle(props)}>{subTitle}</p>
      <div css={Styles.featureItems}>
        <FeatureItems kind="darker" />
      </div>
    </div>
  );
};

export default matchesMediaQuery({
  isMobile: '(max-width: 750px)',
})(SignUpPromo);
